/**
 * Spend Easy - Export & Financial Statement Engine (CSV & PDF)
 * Formats transactions, calculates summaries, and generates clean exports for tax and expense reporting.
 */

import { format } from 'date-fns';

/**
 * Escapes values for standard RFC 4180 CSV compliance
 */
export function sanitizeCSVField(value) {
  if (value === null || value === undefined) {
    return '""';
  }
  const stringValue = String(value);
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return `"${stringValue}"`;
}

/**
 * Formats a transaction date safely
 */
export function formatTxDate(dateVal, dateFormat = 'yyyy-MM-dd') {
  if (!dateVal) return '';
  try {
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    if (!isNaN(d.getTime())) {
      return format(d, dateFormat);
    }
  } catch (e) {}
  return String(dateVal);
}

/**
 * Calculates comprehensive financial statement summaries for a list of transactions
 */
export function calculateStatementSummary(transactions = [], currencySymbol = '$') {
  let totalIncome = 0;
  let totalExpense = 0;
  const categoryMap = {};
  const accountMap = {};

  transactions.forEach((tx) => {
    const amt = parseFloat(tx.amount) || 0;
    const cat = tx.category || 'Uncategorized';
    const acc = tx.account || 'Default Account';

    if (tx.type === 'income') {
      totalIncome += amt;
    } else {
      totalExpense += amt;
      categoryMap[cat] = (categoryMap[cat] || 0) + amt;
    }

    accountMap[acc] = (accountMap[acc] || 0) + (tx.type === 'income' ? amt : -amt);
  });

  const categoryBreakdown = Object.entries(categoryMap)
    .map(([name, amount]) => ({
      name,
      amount,
      percentage: totalExpense > 0 ? Math.round((amount / totalExpense) * 100) : 0,
    }))
    .sort((a, b) => b.amount - a.amount);

  const accountBreakdown = Object.entries(accountMap)
    .map(([name, netBalance]) => ({ name, netBalance }))
    .sort((a, b) => b.netBalance - a.netBalance);

  const netSavings = totalIncome - totalExpense;
  const savingsRate = totalIncome > 0 ? Math.round((netSavings / totalIncome) * 100) : 0;

  return {
    totalIncome,
    totalExpense,
    netSavings,
    savingsRate,
    transactionCount: transactions.length,
    categoryBreakdown,
    accountBreakdown,
    currencySymbol,
  };
}

/**
 * Generates RFC 4180 compliant CSV string for transactions
 */
export function generateTransactionsCSV(transactions = [], options = {}) {
  const {
    includeHeader = true,
    currencySymbol = '$',
    dateRangeLabel = 'All Time',
  } = options;

  const headers = [
    'Date',
    'Type',
    'Description',
    'Category',
    'Account',
    'Amount',
    'Currency',
    'Notes',
  ];

  const rows = transactions.map((t) => {
    const dateStr = formatTxDate(t.date, 'yyyy-MM-dd');
    const typeStr = (t.type || 'expense').toUpperCase();
    const descStr = t.description || t.category || 'Transaction';
    const catStr = t.category || 'Uncategorized';
    const accStr = t.account || 'Default';
    const amtStr = (parseFloat(t.amount) || 0).toFixed(2);
    const currStr = currencySymbol;
    const notesStr = t.notes || (t.toAccount ? `Transfer to ${t.toAccount}` : '');

    return [
      sanitizeCSVField(dateStr),
      sanitizeCSVField(typeStr),
      sanitizeCSVField(descStr),
      sanitizeCSVField(catStr),
      sanitizeCSVField(accStr),
      amtStr,
      sanitizeCSVField(currStr),
      sanitizeCSVField(notesStr),
    ].join(',');
  });

  if (includeHeader) {
    return [headers.join(','), ...rows].join('\r\n');
  }

  return rows.join('\r\n');
}

/**
 * Generates an HTML document optimized for high-resolution printable PDF statements
 */
export function generatePrintableStatementHTML(transactions = [], options = {}) {
  const {
    title = 'Spend Easy - Financial Statement',
    dateRangeLabel = 'Current Period',
    currencySymbol = '$',
    accountName = 'All Accounts',
    userName = 'Valued User',
  } = options;

  const summary = calculateStatementSummary(transactions, currencySymbol);
  const generatedDate = format(new Date(), 'MMMM d, yyyy');

  const rowsHtml = transactions
    .map((t) => {
      const isIncome = t.type === 'income';
      const formattedAmt = `${isIncome ? '+' : '-'}${currencySymbol}${(parseFloat(t.amount) || 0).toFixed(2)}`;
      const amtColor = isIncome ? '#2E7D32' : '#C62828';
      const dateStr = formatTxDate(t.date, 'MMM d, yyyy');

      return `
      <tr>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; color: #4B5563;">${dateStr}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; font-weight: 600; color: #111827;">${t.description || t.category || 'Transaction'}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; color: #4B5563;">
          <span style="background: #F3F4F6; padding: 3px 8px; border-radius: 12px; font-size: 11px;">${t.category || 'General'}</span>
        </td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; color: #6B7280;">${t.account || '—'}</td>
        <td style="padding: 10px 12px; border-bottom: 1px solid #E5E7EB; font-size: 13px; font-weight: 700; text-align: right; color: ${amtColor};">${formattedAmt}</td>
      </tr>
    `;
    })
    .join('');

  const categoryBreakdownHtml = summary.categoryBreakdown
    .slice(0, 6)
    .map(
      (c) => `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 13px;">
      <span style="color: #4B5563;">${c.name} (${c.percentage}%)</span>
      <span style="font-weight: 600; color: #111827;">${currencySymbol}${c.amount.toFixed(2)}</span>
    </div>
    <div style="height: 6px; background: #F3F4F6; border-radius: 3px; overflow: hidden; margin-bottom: 12px;">
      <div style="width: ${c.percentage}%; height: 100%; background: #E8544F; border-radius: 3px;"></div>
    </div>
  `
    )
    .join('');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    @media print {
      body { margin: 0; padding: 20px; font-size: 12px; }
      .no-print { display: none !important; }
      @page { margin: 15mm; size: A4 portrait; }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1F2937;
      background: #FFFFFF;
      margin: 0;
      padding: 32px;
      line-height: 1.5;
    }
    .header-bar {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #E8544F;
      padding-bottom: 20px;
      margin-bottom: 24px;
    }
    .logo-title {
      font-size: 26px;
      font-weight: 800;
      color: #E8544F;
      letter-spacing: -0.5px;
    }
    .kpi-container {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      margin-bottom: 28px;
    }
    .kpi-card {
      background: #F9FAFB;
      border: 1px solid #E5E7EB;
      border-radius: 10px;
      padding: 14px;
      text-align: center;
    }
    .kpi-label {
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 700;
      color: #6B7280;
      margin-bottom: 6px;
    }
    .kpi-val {
      font-size: 18px;
      font-weight: 800;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }
    th {
      background: #F9FAFB;
      text-align: left;
      padding: 10px 12px;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      color: #6B7280;
      border-bottom: 2px solid #E5E7EB;
    }
    .footer {
      margin-top: 40px;
      border-top: 1px solid #E5E7EB;
      padding-top: 16px;
      font-size: 11px;
      color: #9CA3AF;
      display: flex;
      justify-content: space-between;
    }
  </style>
</head>
<body>
  <div class="header-bar">
    <div>
      <div class="logo-title">SPEND EASY</div>
      <div style="font-size: 14px; font-weight: 600; color: #4B5563; margin-top: 4px;">Official Statement of Accounts</div>
      <div style="font-size: 12px; color: #6B7280; margin-top: 2px;">Account: ${accountName} • Period: ${dateRangeLabel}</div>
    </div>
    <div style="text-align: right;">
      <div style="font-size: 12px; color: #6B7280;">Statement Date</div>
      <div style="font-size: 14px; font-weight: 700; color: #111827;">${generatedDate}</div>
      <div style="font-size: 12px; color: #6B7280; margin-top: 4px;">User: ${userName}</div>
    </div>
  </div>

  <div class="kpi-container">
    <div class="kpi-card">
      <div class="kpi-label">Total Income</div>
      <div class="kpi-val" style="color: #2E7D32;">+${currencySymbol}${summary.totalIncome.toFixed(2)}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Total Expense</div>
      <div class="kpi-val" style="color: #C62828;">-${currencySymbol}${summary.totalExpense.toFixed(2)}</div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Net Savings</div>
      <div class="kpi-val" style="color: ${summary.netSavings >= 0 ? '#111827' : '#C62828'};">
        ${summary.netSavings >= 0 ? '+' : '-'}${currencySymbol}${Math.abs(summary.netSavings).toFixed(2)}
      </div>
    </div>
    <div class="kpi-card">
      <div class="kpi-label">Transactions</div>
      <div class="kpi-val" style="color: #4B5563;">${summary.transactionCount}</div>
    </div>
  </div>

  ${
    summary.categoryBreakdown.length > 0
      ? `
  <div style="background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 10px; padding: 16px; margin-bottom: 24px;">
    <div style="font-size: 13px; font-weight: 700; text-transform: uppercase; color: #374151; margin-bottom: 14px;">Top Expense Categories</div>
    ${categoryBreakdownHtml}
  </div>
  `
      : ''
  }

  <div style="font-size: 14px; font-weight: 700; text-transform: uppercase; color: #374151; margin-bottom: 8px;">
    Transaction Details (${summary.transactionCount})
  </div>
  <table>
    <thead>
      <tr>
        <th style="width: 18%;">Date</th>
        <th style="width: 32%;">Description</th>
        <th style="width: 20%;">Category</th>
        <th style="width: 15%;">Account</th>
        <th style="width: 15%; text-align: right;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || '<tr><td colspan="5" style="text-align: center; padding: 24px; color: #9CA3AF;">No transactions found for the selected period.</td></tr>'}
    </tbody>
  </table>

  <div class="footer">
    <div>Generated automatically by Spend Easy • Confidential Financial Record</div>
    <div>Page 1 of 1</div>
  </div>
</body>
</html>
  `.trim();
}
